import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

export interface ClusterComboboxOption {
  value: string;
  label: string;
  logoUrl?: string;
}

export interface ClusterComboboxProps {
  value: string;
  onChange: (value: string) => void;
  options: (string | ClusterComboboxOption)[];
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  style?: React.CSSProperties;
  allowFreeText?: boolean;
  noDataText?: string;
  onSearchChange?: (search: string) => void;
}

const ClusterCombobox: React.FC<ClusterComboboxProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select or type project cluster...",
  disabled = false,
  isLoading = false,
  className = "",
  style,
  allowFreeText = true,
  noDataText = "Tidak ada data cluster",
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizedOptions: ClusterComboboxOption[] = useMemo(
    () =>
      options.map((opt) =>
        typeof opt === "string" ? { value: opt, label: opt } : opt
      ),
    [options]
  );

  const selectedOption = useMemo(
    () =>
      normalizedOptions.find(
        (opt) => opt.value.toLowerCase() === (value || "").toLowerCase()
      ),
    [normalizedOptions, value]
  );

  // Sync external value changes
  useEffect(() => {
    if (allowFreeText) {
      setInputValue(value);
    } else {
      setInputValue(selectedOption ? selectedOption.label : value || "");
    }
  }, [value, allowFreeText, selectedOption]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        if (!allowFreeText) {
          setInputValue(selectedOption ? selectedOption.label : "");
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [allowFreeText, selectedOption]);

  const filteredOptions = inputValue.trim()
    ? normalizedOptions.filter(
        (opt) =>
          opt.label.toLowerCase().includes(inputValue.toLowerCase()) ||
          opt.value.toLowerCase().includes(inputValue.toLowerCase())
      )
    : normalizedOptions;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = e.target.value;
      setInputValue(val);
      if (allowFreeText) {
        onChange(val);
      } else if (val === "") {
        onChange("");
      }
      onSearchChange?.(val);
      if (!isOpen) setIsOpen(true);
    },
    [onChange, isOpen, allowFreeText, onSearchChange]
  );

  const handleSelect = useCallback(
    (opt: ClusterComboboxOption) => {
      setInputValue(opt.label);
      onChange(opt.value);
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onChange]
  );

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      setIsOpen(false);
      if (!allowFreeText) {
        setInputValue(selectedOption ? selectedOption.label : "");
      }
      inputRef.current?.blur();
    }
  };

  return (
    <div
      ref={containerRef}
      className={`position-relative ${className}`}
      style={style}
    >
      <div className="position-relative d-flex align-items-center">
        {selectedOption?.logoUrl && !isOpen && (
          <img
            src={selectedOption.logoUrl}
            alt={selectedOption.label}
            className="position-absolute ms-2"
            style={{
              width: "24px",
              height: "24px",
              objectFit: "contain",
              pointerEvents: "none",
            }}
          />
        )}
        <input
          ref={inputRef}
          type="text"
          className="form-control form-control-compact"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          style={{
            paddingLeft: selectedOption?.logoUrl && !isOpen ? "2.4rem" : undefined,
            borderColor: isOpen ? "#2B75DA" : undefined,
            boxShadow: isOpen ? "0 0 0 2px rgba(43, 117, 218, 0.15)" : undefined,
            transition: "border-color 0.15s ease, box-shadow 0.15s ease",
          }}
        />
      </div>

      {isOpen && (
        <div
          className="position-absolute w-100 bg-white border rounded-3 shadow-lg mt-1 animate-pop-in"
          style={{
            zIndex: 1050,
            maxHeight: "260px",
            overflowY: "auto",
          }}
        >
          {isLoading ? (
            <div className="px-3 py-2 small text-muted">Loading...</div>
          ) : filteredOptions.length === 0 ? (
            <div className="px-3 py-2 small text-muted">
              {allowFreeText && inputValue.trim() ? (
                <>
                  Tekan Enter atau klik di luar untuk menggunakan "
                  <strong>{inputValue}</strong>"
                </>
              ) : (
                noDataText
              )}
            </div>
          ) : (
            filteredOptions.map((opt, idx) => {
              const isSelected =
                opt.value.toLowerCase() === (value || "").toLowerCase();
              return (
                <button
                  key={idx}
                  type="button"
                  className={`dropdown-item rounded-0 px-3 py-2 text-start d-flex align-items-center gap-2 ${
                    isSelected
                      ? "active bg-primary text-white fw-semibold"
                      : "text-dark"
                  }`}
                  style={{
                    fontSize: "0.85rem",
                    height: "auto",
                    minHeight: "fit-content",
                    border: "none",
                    minWidth: "unset",
                  }}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.logoUrl && (
                    <img
                      src={opt.logoUrl}
                      alt={opt.label}
                      className="rounded bg-white p-1 border flex-shrink-0"
                      style={{
                        width: "28px",
                        height: "28px",
                        objectFit: "contain",
                      }}
                    />
                  )}
                  <span>{opt.label}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default ClusterCombobox;
